/* ==========================================================================
   FILE: global/components/Toggle.jsx
   IAMONEAI - Toggle/Switch Component
   On/off switch with labels and sizes
   ========================================================================== */

import React, { forwardRef, useId } from 'react';
import './Toggle.css';

// ==========================================================================
// TOGGLE COMPONENT
// ==========================================================================

/**
 * Toggle/Switch component
 * 
 * @param {Object} props
 * @param {boolean} checked - Whether toggle is on
 * @param {Function} onChange - Change handler
 * @param {string} label - Toggle label
 * @param {string} description - Additional description text
 * @param {'sm'|'md'|'lg'} size - Toggle size
 * @param {boolean} disabled - Whether toggle is disabled
 * @param {'left'|'right'} labelPosition - Position of label relative to toggle
 * 
 * @example
 * <Toggle
 *   checked={isEnabled}
 *   onChange={(e) => setIsEnabled(e.target.checked)}
 *   label="Enable notifications"
 * />
 */
const Toggle = forwardRef(({
  checked = false,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  labelPosition = 'right',
  className = '',
  id: providedId,
  name,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  const wrapperClasses = [
    'toggle-wrapper',
    `toggle-label-${labelPosition}`,
    disabled && 'toggle-disabled',
    className,
  ].filter(Boolean).join(' ');

  const toggleClasses = [
    'toggle',
    `toggle-${size}`,
    checked && 'toggle-checked',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      <label htmlFor={id} className="toggle-label-container">
        {/* Toggle Switch */}
        <span className={toggleClasses}>
          <input
            ref={ref}
            type="checkbox"
            id={id}
            name={name}
            className="toggle-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
            {...props}
          />
          <span className="toggle-track">
            <span className="toggle-thumb" />
          </span>
        </span>

        {/* Label & Description */}
        {(label || description) && (
          <span className="toggle-text">
            {label && <span className="toggle-label-text">{label}</span>}
            {description && <span className="toggle-description">{description}</span>}
          </span>
        )}
      </label>
    </div>
  );
});

Toggle.displayName = 'Toggle';

// ==========================================================================
// TOGGLE GROUP COMPONENT
// ==========================================================================

/**
 * Group of toggles with shared styling
 * 
 * @example
 * <ToggleGroup label="Notifications">
 *   <Toggle label="Email" checked={emailEnabled} onChange={...} />
 *   <Toggle label="Push" checked={pushEnabled} onChange={...} />
 *   <Toggle label="SMS" checked={smsEnabled} onChange={...} />
 * </ToggleGroup>
 */
export function ToggleGroup({
  label,
  children,
  className = '',
}) {
  const groupClasses = [
    'toggle-group',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={groupClasses} role="group" aria-label={label}>
      {label && <span className="toggle-group-label">{label}</span>}
      <div className="toggle-group-items">
        {children}
      </div>
    </div>
  );
}

// ==========================================================================
// TOGGLE CARD COMPONENT
// ==========================================================================

/**
 * Toggle inside a card-like container
 * 
 * @example
 * <ToggleCard
 *   checked={darkMode}
 *   onChange={(e) => setDarkMode(e.target.checked)}
 *   title="Dark Mode"
 *   description="Use dark theme throughout the app"
 *   icon={<MoonIcon />}
 * />
 */
export function ToggleCard({
  checked = false,
  onChange,
  title,
  description,
  icon,
  disabled = false,
  className = '',
}) {
  const cardClasses = [
    'toggle-card',
    checked && 'toggle-card-active',
    disabled && 'toggle-card-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      <label className="toggle-card-label">
        {/* Icon */}
        {icon && (
          <span className="toggle-card-icon">
            {icon}
          </span>
        )}

        {/* Content */}
        <span className="toggle-card-content">
          {title && <span className="toggle-card-title">{title}</span>}
          {description && <span className="toggle-card-description">{description}</span>}
        </span>

        {/* Toggle */}
        <Toggle
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          size="md"
        />
      </label>
    </div>
  );
}

// ==========================================================================
// RADIO TOGGLE GROUP (Single Selection)
// ==========================================================================

/**
 * Group of toggles where only one can be selected (like radio buttons)
 * 
 * @example
 * <RadioToggleGroup
 *   name="theme"
 *   value={theme}
 *   onChange={setTheme}
 *   options={[
 *     { value: 'light', label: 'Light' },
 *     { value: 'dark', label: 'Dark' },
 *     { value: 'system', label: 'System' },
 *   ]}
 * />
 */
export function RadioToggleGroup({
  name,
  value,
  onChange,
  options = [],
  size = 'md',
  disabled = false,
  className = '',
}) {
  const groupClasses = [
    'radio-toggle-group',
    `radio-toggle-group-${size}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={groupClasses} role="radiogroup">
      {options.map((option) => (
        <label
          key={option.value}
          className={[
            'radio-toggle-option',
            value === option.value && 'radio-toggle-option-active',
            disabled && 'radio-toggle-option-disabled',
          ].filter(Boolean).join(' ')}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            className="radio-toggle-input"
          />
          {option.icon && <span className="radio-toggle-icon">{option.icon}</span>}
          <span className="radio-toggle-label">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default Toggle;